import { Controller } from "@hotwired/stimulus";
import {
   CHANGELOG_SEEN_EVENT,
   readWatermark,
   shouldShowAttentionMark,
   type ChangelogSeenDetail,
   type StorageLike,
} from "./changelog-watermark";

/**
 * Top-bar attention mark for the Changelog control. Dot is visible only when the shared badge rule
 * says so (entries exist and browser watermark ≠ newest identity). Never shows a count.
 *
 * Listens for `changelog:seen` so mark-seen on the page clears the dot while this layout control
 * stays mounted under Turbo morph (connect is not re-run).
 */
export default class extends Controller {
   static targets = ["dot"];
   static values = {
      app: String,
      newestIdentity: String,
   };

   declare readonly dotTarget: HTMLElement;
   declare readonly hasDotTarget: boolean;
   declare readonly appValue: string;
   declare readonly newestIdentityValue: string;
   declare readonly hasNewestIdentityValue: boolean;

   private onSeenBound = (event: Event) => this.onSeen(event);

   connect() {
      window.addEventListener(CHANGELOG_SEEN_EVENT, this.onSeenBound);
      this.refresh();
   }

   disconnect() {
      window.removeEventListener(CHANGELOG_SEEN_EVENT, this.onSeenBound);
   }

   refresh() {
      if (!this.hasDotTarget) return;

      const storage = this.resolveStorage();
      const watermark = storage ? readWatermark(storage, this.appValue) : null;
      const newest = this.hasNewestIdentityValue ? this.newestIdentityValue : null;
      const show = shouldShowAttentionMark(newest, watermark);

      this.dotTarget.classList.toggle("hidden", !show);
   }

   private onSeen(event: Event) {
      const detail = (event as CustomEvent<ChangelogSeenDetail>).detail;
      if (detail?.app && detail.app !== this.appValue) return;
      this.refresh();
   }

   private resolveStorage(): StorageLike | null {
      try {
         return window.localStorage;
      } catch {
         return null;
      }
   }
}
